/**
 * @file CorsProxyInterceptorSimple.js
 * @description Interceptor minimalista para manejar errores CORS y aplicar proxy
 * automáticamente cuando sea necesario.
 * @version 1.0.0
 * @author Nubelytics Engineering Team
 */

import corsProxyConfig from './CorsProxyConfig';

/**
 * Configuración global para el servicio de proxy CORS
 */
const { ALWAYS_USE_PROXY_DOMAINS, IMPORTANT_HEADERS } = corsProxyConfig;
// Atajar referencias a métodos para que mantengan el contexto correcto del singleton
const shouldAlwaysUseProxy = corsProxyConfig.shouldAlwaysUseProxy.bind(corsProxyConfig);
const generateProxyUrl = corsProxyConfig.generateProxyUrl.bind(corsProxyConfig);

/**
 * Interceptor simplificado para gestionar errores CORS en solicitudes Axios
 * 
 * @class CorsProxyInterceptorSimple
 * @example
 * // Registrar interceptor para una instancia de Axios
 * const axiosInstance = axios.create({ ... });
 * CorsProxyInterceptorSimple.register(axiosInstance);
 */
class CorsProxyInterceptorSimple {
  /**
   * Registra los interceptores en la instancia de Axios proporcionada
   * @param {Object} axiosInstance - Instancia de Axios a interceptar
   * @returns {Object} Objeto con los IDs de los interceptores para desregistrar si es necesario
   */
  static register(axiosInstance) {
    if (!axiosInstance || !axiosInstance.interceptors) {
      console.error('[CorsProxyInterceptorSimple] Instancia de Axios inválida');
      return { request: null, response: null };
    }
    
    // Set para evitar bucles infinitos de reintentos
    const retryCache = new Set();
    
    // Interceptor de solicitud
    const requestId = axiosInstance.interceptors.request.use((config) => {
      // Si ya fue procesada por un reintento, evitar reprocesamiento
      if (config._retryProcessed) {
        return config;
      }
      
      // Generar un ID único para esta solicitud para seguimiento
      config._requestId = `${config.method || 'GET'}-${config.url}-${Date.now()}`;
      
      // Resolver URL absoluta para evaluación de dominio y diagnóstico
      let absoluteUrl = config.url;
      if (absoluteUrl && !/^https?:\/\//i.test(absoluteUrl)) {
        try {
          if (config.baseURL && /^https?:\/\//i.test(config.baseURL)) {
            absoluteUrl = new URL(config.url, config.baseURL).toString();
          } else if (typeof window !== 'undefined' && window.location) {
            absoluteUrl = new URL(config.url, window.location.origin).toString();
          }
        } catch (e) {
          console.warn('[CORS] No se pudo resolver URL absoluta:', e?.message || e);
        }
      }
      // Guardar la URL original (absoluta) para diagnóstico
      config._originalUrl = absoluteUrl;
      
      // Agregar información sobre la versión
      if (config.url && (config.url.includes('/api/') || config.url.includes('nubelytics.com'))) {
        if (!config.headers) config.headers = {};
        config.headers['x-monitor-check'] = 'true';
        config.headers['x-app-version'] = process.env.REACT_APP_VERSION || '1.0.0';
      }
      
      // Evitar doble proxy si ya apunta al endpoint del proxy
      const proxyEndpoint = corsProxyConfig.getResolvedProxyEndpoint();
      const alreadyProxied = proxyEndpoint && absoluteUrl?.startsWith(proxyEndpoint);

      // Si la URL pertenece a un dominio marcado como seguro, omitir cualquier lógica de proxy forzado
      if (corsProxyConfig.isSafeDomain(absoluteUrl)) {
        return config;
      }
      
      // Verificar si el dominio debe usar siempre el proxy y no está ya proxificado
      if (!alreadyProxied && shouldAlwaysUseProxy(absoluteUrl)) {
        // Generar URL del proxy
        const proxyUrl = generateProxyUrl(absoluteUrl);
        config.url = proxyUrl;
        
        // Marcar que ya se ha aplicado el proxy
        config._appliedProxy = true;
        config._proxyReason = 'forced_domain';
        
        // Añadir headers diagnósticos
        if (!config.headers) config.headers = {};
        config.headers['x-proxy-applied'] = 'true';
        config.headers['x-proxy-reason'] = 'forced_domain';
        
        console.info(`[CORS] Usando proxy para: ${config._originalUrl}`);
      }
      
      return config;
    }, (error) => Promise.reject(error));
    
    // Interceptor de respuesta
    const responseId = axiosInstance.interceptors.response.use(
      // Éxito - no hacer nada
      (response) => response,
      
      // Error - detectar problemas CORS y reintentar con proxy
      (error) => {
        // Si no hay error o config, rechazar directamente
        if (!error || !error.config) {
          return Promise.reject(error);
        }
        
        const config = error.config;

      // Si la respuesta tiene un status >= 400 (y distinto de 0), asumimos que es un
      // error HTTP legítimo del backend (no relacionado con CORS) y NO reintentamos
      // la solicitud utilizando el proxy. Esto evita reintentos innecesarios que
      // podían terminar en 502 cuando el proxy recibía un 404/401 legítimo.
      if (error.response && typeof error.response.status === 'number' && error.response.status >= 400) {
        return Promise.reject(error);
      }
        
        // Verificar si podemos usar el proxy (no se ha usado ya y no está en caché)
        const canUseProxy = !config._appliedProxy && !retryCache.has(config.url);
        
        // Detectar problemas CORS
        const isCorsError = 
          (!error.response && error.message && error.message.toLowerCase().includes('cors')) ||
          (error.message && error.message.toLowerCase().includes('network error')) ||
          (error.response && error.response.status === 0) ||
          (error.message && error.message.toLowerCase().includes('header not allowed'));
        
        // Si es un error CORS y podemos reintentar con proxy
        if (isCorsError && canUseProxy) {
          console.warn(`[CORS] Error detectado, reintentando con proxy: ${config.url}`);
          
          // Marcar en caché para evitar bucles
          retryCache.add(config.url);
          
          // Resolver URL absoluta para retry
          let retryAbsoluteUrl = config._originalUrl || config.url;
          if (retryAbsoluteUrl && !/^https?:\/\//i.test(retryAbsoluteUrl)) {
            try {
              if (config.baseURL && /^https?:\/\//i.test(config.baseURL)) {
                retryAbsoluteUrl = new URL(retryAbsoluteUrl, config.baseURL).toString();
              } else if (typeof window !== 'undefined' && window.location) {
                retryAbsoluteUrl = new URL(retryAbsoluteUrl, window.location.origin).toString();
              }
            } catch(e) {}
          }
          // Evitar doble proxy en retry
          const proxyEndpoint = corsProxyConfig.getResolvedProxyEndpoint();
          if (proxyEndpoint && retryAbsoluteUrl.startsWith(proxyEndpoint)) {
            return Promise.reject(error);
          }
          // Generar URL del proxy
          const proxyUrl = generateProxyUrl(retryAbsoluteUrl);
          
          // Crear nueva config para el reintento
          const retryConfig = { ...config };
          retryConfig.url = proxyUrl;
          retryConfig._retryProcessed = true;
          retryConfig._appliedProxy = true;
          
          // Eliminar headers problemáticos
          if (retryConfig.headers) {
            ['origin', 'referer', 'host'].forEach(header => {
              delete retryConfig.headers[header];
            });
          }
          
          // Reintentar con la nueva configuración
          return axiosInstance.request(retryConfig);
        }
        
        // Si no podemos reintentar, rechazar con el error original
        return Promise.reject(error);
      }
    );
    
    return {
      request: requestId,
      response: responseId,
      unregister: () => {
        axiosInstance.interceptors.request.eject(requestId);
        axiosInstance.interceptors.response.eject(responseId);
      }
    };
  }
}

export default CorsProxyInterceptorSimple;
